import {Component, computed, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {ProjectStepStateService} from '../project-step-state.service';
import {Chapter} from '../project-step.types';
import {ProjectBannerComponent} from '../project-banner/project-banner.component';
import {ProjectContentBodyComponent} from '../project-content-body/project-content-body.component';
import {ContentWrapperComponent} from '../../../../shared/components/content-wrapper/content-wrapper.component';
import {SaveButtonTrackingService} from '../../../../shared/project-step-save-button/save-button-tracking.service';
import {ConfirmationDialogService} from '../../../../shared/components/dialog/confirmation/confirmation-dialog.service';

type NavigationDirection = 'next' | 'previous';

@Component({
  selector: 'app-project-content',
  imports: [
    CommonModule,
    TranslocoPipe,
    ProjectBannerComponent,
    ProjectContentBodyComponent,
    ContentWrapperComponent,
  ],
  templateUrl: './project-content.component.html',
})
export class ProjectContentComponent {
  private readonly stepStateService = inject(ProjectStepStateService);
  private readonly confirmDialogService = inject(ConfirmationDialogService);
  private readonly translocoService = inject(TranslocoService);
  private readonly saveButtonTrackingService = inject(SaveButtonTrackingService);

  activeSpecialOption = computed(() => this.stepStateService.activeSpecialOption());
  specialOptions = computed(() => this.stepStateService.specialOptions());
  protected readonly activeSpecialOptionTitle = computed(() => {
    const activeOptionId = this.stepStateService.activeSpecialOption();
    if (!activeOptionId) return '';
    const option = this.stepStateService.specialOptions().find((o) => o.id === activeOptionId);
    return option ? option.title : '';
  });

  protected readonly activeStep = computed(() => this.stepStateService.activeStep());
  protected readonly activeStepTitle = computed(() => {
    const active = this.stepStateService.activeStep();
    if (!active) return '';

    const chapter = this.stepStateService
      .chaptersWithStatuses()
      .find((ch) => ch.configId === active.chapterId);
    if (!chapter) return '';

    const step = chapter.steps.find((s) => s.configId === active.stepId);
    if (!step) return '';

    return `${chapter.chapterUiNumber}.${step.stepUiNumber}. ${step.translatedTitle}`;
  });

  protected readonly activeSpecialOptionalBackgroundImage = computed(() => {
    const directusChapter = this.stepStateService
      ._projectStepsDirectus()
      ?.find((step) => step.sort_order === 1);
    return this.stepStateService.getChapterBackgroundImage(directusChapter);
  });

  protected readonly activeStepBackgroundImage = computed(() => {
    return this.stepStateService.activeChapterBackgroundImage();
  });

  onNavigateNext() {
    this.navigate('next');
  }

  onNavigatePrev() {
    this.navigate('previous');
  }

  private navigate(direction: NavigationDirection): void {
    const active = this.stepStateService.activeStep();
    if (!active) return;

    const chapters = this.stepStateService.chaptersWithStatuses();
    const currentChapterIndex = chapters.findIndex((c) => c.configId === active.chapterId);
    const currentChapter = chapters[currentChapterIndex];

    if (!currentChapter) return;

    const currentStepIndex = currentChapter.steps.findIndex((s) => s.configId === active.stepId);

    const continueNavigation = () => {
      if (direction === 'next') {
        this.navigateToNextStep(currentChapter, currentStepIndex, chapters, currentChapterIndex);
      } else {
        this.navigateToPreviousStep(
          currentChapter,
          currentStepIndex,
          chapters,
          currentChapterIndex,
        );
      }
    };

    if (this.stepStateService.isNavigationConfirmationRequired()) {
      this.confirmDialogService.confirm({
        headerKey: `project.confirmStepChange.header`,
        messageKey: `project.confirmStepChange.message`,
        accept: continueNavigation,
        reject: () => this.saveButtonTrackingService.requestReveal(),
      });
    } else {
      continueNavigation();
    }
  }

  private navigateToNextStep(
    currentChapter: Chapter,
    currentStepIndex: number,
    chapters: Chapter[],
    currentChapterIndex: number,
  ): void {
    if (currentStepIndex < currentChapter.steps.length - 1) {
      const nextStep = currentChapter.steps[currentStepIndex + 1];
      this.stepStateService.setActiveStep(currentChapter.configId, nextStep.configId);
      return;
    }

    if (currentChapterIndex < chapters.length - 1) {
      const nextChapter = chapters[currentChapterIndex + 1];
      const firstStepInNextChapter = nextChapter.steps[0];
      if (firstStepInNextChapter) {
        this.stepStateService.setActiveStep(nextChapter.configId, firstStepInNextChapter.configId);
      }
    }
  }

  private navigateToPreviousStep(
    currentChapter: Chapter,
    currentStepIndex: number,
    chapters: Chapter[],
    currentChapterIndex: number,
  ): void {
    if (currentStepIndex > 0) {
      const prevStep = currentChapter.steps[currentStepIndex - 1];
      this.stepStateService.setActiveStep(currentChapter.configId, prevStep.configId);
      return;
    }

    if (currentChapterIndex > 0) {
      const prevChapter = chapters[currentChapterIndex - 1];
      const lastStepInPrevChapter = prevChapter.steps[prevChapter.steps.length - 1];
      if (lastStepInPrevChapter) {
        this.stepStateService.setActiveStep(prevChapter.configId, lastStepInPrevChapter.configId);
      }
    }
  }
}
